import { useState } from 'react';
import { ChevronRight, CircleAlert, CircleDot, Hash, MoveHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Button,
    ScrollArea,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    cn,
} from 'shared-ui';

import type { PoiPipelinesState } from '@/hooks/usePoiPipelines';
import { formatNetFunc, formatPipeLength } from '../../utils/pipelineFeatureProperties';

function RowSkeleton() {
    return (
        <div className="flex items-center gap-3 border-b py-3" aria-hidden="true">
            <span className="bg-muted block h-4 w-16 animate-pulse rounded" />
            <span className="bg-muted block h-4 w-24 animate-pulse rounded" />
            <span className="bg-muted ml-auto block h-4 w-14 animate-pulse rounded" />
        </div>
    );
}

/**
 * Values stay on one line via truncation so long ones can't skew the layout or run
 * underneath neighbouring cells; when a value is actually clipped, hovering reveals
 * it in full in a tooltip. Truncation is measured on hover rather than mount so the
 * check reflects the live layout.
 */
function TruncatedValue({ value, className }: { value: string; className?: string }) {
    const [isTruncated, setIsTruncated] = useState(false);

    return (
        <Tooltip>
            <TooltipTrigger
                render={
                    <span
                        className={cn('truncate', className)}
                        onMouseEnter={(event) => {
                            setIsTruncated(event.currentTarget.scrollWidth > event.currentTarget.clientWidth);
                        }}
                    >
                        {value}
                    </span>
                }
            />
            {isTruncated && <TooltipContent side="top">{value}</TooltipContent>}
        </Tooltip>
    );
}

function ExpandedField({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
    return (
        <div className="flex min-w-0 items-start gap-2.5">
            <span
                className="bg-background flex size-10 shrink-0 items-center justify-center rounded-md border"
                aria-hidden="true"
            >
                <Icon className="text-origin-blue size-4" />
            </span>
            <div className="min-w-0">
                <span className="text-foreground block text-sm leading-5 font-medium">{label}</span>
                <TruncatedValue value={value} className="text-muted-foreground block text-sm leading-5 font-normal" />
            </div>
        </div>
    );
}

interface PoiPipelinesTabProps {
    state: PoiPipelinesState & { retry: () => void };
}

/**
 * The Pipelines tab body: the associated-pipelines breakdown for the selected POI, from
 * the same response that drives the map's radius display (fetched on selection, so this
 * tab never fires its own request).
 *
 * Layout: the info banner and column headings scroll with the list; the total stays in a
 * fixed footer below the scroll area so expanding rows can never push it off-screen.
 */
export function PoiPipelinesTab({ state }: PoiPipelinesTabProps) {
    const { data, isLoading, isError, retry } = state;
    const pipelines = data?.pipelines ?? [];

    return (
        <>
            <ScrollArea className="min-h-0 flex-1">
                <div className="px-6 py-4">
                    <div className="border-origin-blue bg-origin-blue/8 mb-4 flex gap-3 rounded-lg border px-4 py-3">
                        <CircleAlert className="text-origin-blue mt-0.5 size-4 shrink-0" aria-hidden="true" />
                        <div>
                            <p className="text-origin-blue text-sm leading-5 font-bold">Associated Pipelines</p>
                            <p className="text-origin-blue text-sm leading-5 font-normal">
                                View the pipelines associated with the selected Point of Interest (POI) below
                            </p>
                        </div>
                    </div>

                    {isLoading && (
                        <div role="status" aria-label="Loading pipelines">
                            <RowSkeleton />
                            <RowSkeleton />
                            <RowSkeleton />
                        </div>
                    )}

                    {isError && (
                        <div className="space-y-3">
                            <p className="text-destructive text-sm">Unable to load pipelines — please try again.</p>
                            <Button
                                type="button"
                                variant="ghost"
                                className="panel-close-btn text-primary h-10 cursor-pointer"
                                onClick={retry}
                            >
                                Try Again
                            </Button>
                        </div>
                    )}

                    {data && pipelines.length === 0 && (
                        <p className="text-muted-foreground text-sm">
                            No pipelines within {data.radiusMetres} m of this Point of Interest.
                        </p>
                    )}

                    {data && pipelines.length > 0 && (
                        <>
                            <div
                                className="text-foreground grid grid-cols-[124px_1fr_auto] gap-2 border-b pb-2 text-sm leading-5 font-bold"
                                aria-hidden="true"
                            >
                                <span className="pl-2">Pipe ID</span>
                                <span>Pipe Type</span>
                                <span className="text-right">Length</span>
                            </div>
                            <Accordion multiple={false}>
                                {pipelines.map((pipeline) => (
                                    <AccordionItem key={pipeline.pipelineId} value={pipeline.pipelineId}>
                                        <AccordionTrigger
                                            className="group/pipeline-row cursor-pointer py-0 hover:no-underline **:data-[slot=accordion-trigger-icon]:hidden!"
                                            aria-label={`${pipeline.pipelineId}, ${formatNetFunc(pipeline.netFunc)}, ${
                                                formatPipeLength(pipeline.lengthMetres) ?? 'length unavailable'
                                            }`}
                                        >
                                            <span className="grid w-full grid-cols-[124px_1fr_auto] items-center gap-2 text-sm">
                                                <span className="group-aria-expanded/pipeline-row:text-origin-blue group-hover/pipeline-row:text-origin-blue flex min-w-0 items-center gap-2.5 font-medium">
                                                    <span
                                                        className="flex size-10 shrink-0 items-center justify-center"
                                                        aria-hidden="true"
                                                    >
                                                        <ChevronRight className="text-foreground group-aria-expanded/pipeline-row:text-origin-blue group-hover/pipeline-row:text-origin-blue size-4 transition-transform group-aria-expanded/pipeline-row:-rotate-90" />
                                                    </span>
                                                    <TruncatedValue value={pipeline.pipelineId} />
                                                </span>
                                                <span className="text-muted-foreground group-aria-expanded/pipeline-row:text-origin-blue group-hover/pipeline-row:text-origin-blue">
                                                    {formatNetFunc(pipeline.netFunc)}
                                                </span>
                                                <span className="text-muted-foreground group-aria-expanded/pipeline-row:text-origin-blue group-hover/pipeline-row:text-origin-blue text-right">
                                                    {formatPipeLength(pipeline.lengthMetres) ?? '—'}
                                                </span>
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div className="p-3">
                                                <div className="grid grid-cols-2 gap-x-2 gap-y-4">
                                                    <ExpandedField
                                                        icon={Hash}
                                                        label="Pipe ID"
                                                        value={pipeline.pipelineId}
                                                    />
                                                    <ExpandedField
                                                        icon={CircleDot}
                                                        label="Pipe Type"
                                                        value={formatNetFunc(pipeline.netFunc)}
                                                    />
                                                    <ExpandedField
                                                        icon={MoveHorizontal}
                                                        label="Length"
                                                        value={formatPipeLength(pipeline.lengthMetres) ?? '—'}
                                                    />
                                                </div>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </>
                    )}
                </div>
            </ScrollArea>
            {data && pipelines.length > 0 && (
                <div className="flex shrink-0 items-center justify-between border-t px-6 py-3">
                    <span className="text-foreground text-sm leading-5 font-bold">Total Pipe Length</span>
                    <span
                        className="text-foreground text-sm leading-5 font-bold"
                        aria-label="Total pipeline length within the radius"
                    >
                        {formatPipeLength(data.totalLengthMetres) ?? '—'}
                    </span>
                </div>
            )}
        </>
    );
}
